import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { ArrowLeft, DollarSign, Users, UserMinus, Target, TrendingUp, TrendingDown, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import { TCSLogo } from '../components/TCSLogo';
import { NavArrows } from '../components/NavArrows';

const MONTHS_PT = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

const formatBRL = (v) =>
  (v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const PLAN_COLORS = {
  bronze: '#00F0FF',
  prata: '#C0C7D1',
  ouro: '#FFB800',
};

const AdminFinancial = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_BASE}/api/admin/financial`, {
        params: { year, month },
      });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Erro ao carregar faturamento');
      toast.error('Erro ao carregar faturamento');
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    if (user && user.role === 'admin') load();
  }, [load, user]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const yearsRange = (() => {
    const curr = today.getFullYear();
    const arr = [];
    for (let y = curr - 4; y <= curr + 1; y++) arr.push(y);
    return arr;
  })();

  const received = data?.revenue?.received ?? 0;
  const target = data?.revenue?.target ?? 5000;
  const delta = data?.revenue?.delta_vs_target ?? 0;
  const isProfit = data?.revenue?.status_level === 'profit';
  const pct = Math.min(100, Math.max(0, (received / target) * 100));
  const barColor = isProfit ? '#39FF14' : '#FF3366';

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <header className="sticky top-0 z-50 bg-black/60 backdrop-blur-xl border-b border-white/10">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <NavArrows />
            <TCSLogo size="sm" showTagline={false} />
            <div>
              <h1
                className="text-xl md:text-2xl font-black uppercase tracking-tighter text-[#39FF14]"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                Faturamento Mensal
              </h1>
              <p className="text-xs tracking-[0.2em] uppercase text-[#A1A1AA]">
                Relatório isolado · meta R$ 5.000
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate('/admin')}
              variant="outline"
              className="bg-transparent border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF]/10 rounded-none hidden md:inline-flex"
              data-testid="financial-back-admin"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Painel Admin
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              size="icon"
              className="bg-transparent border-[#FF3366] text-[#FF3366] hover:bg-[#FF3366]/10 rounded-none"
              data-testid="financial-logout"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 space-y-8">
        {/* Period selector */}
        <section className="bg-[#121212] border border-white/10 p-5" data-testid="financial-period-selector">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#A1A1AA] mb-3">
            Período de Análise
          </p>
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
            <div className="flex items-center gap-2">
              <label className="text-xs uppercase text-[#A1A1AA]" htmlFor="fin-month">Mês</label>
              <select
                id="fin-month"
                value={month}
                onChange={(e) => setMonth(Number(e.target.value))}
                className="bg-black border border-white/20 text-white px-3 py-2 rounded-none focus:border-[#39FF14] focus:outline-none"
                data-testid="financial-month-select"
              >
                {MONTHS_PT.map((label, idx) => (
                  <option key={label} value={idx + 1}>{label}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs uppercase text-[#A1A1AA]" htmlFor="fin-year">Ano</label>
              <select
                id="fin-year"
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                className="bg-black border border-white/20 text-white px-3 py-2 rounded-none focus:border-[#39FF14] focus:outline-none"
                data-testid="financial-year-select"
              >
                {yearsRange.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            {data && (
              <div className="sm:ml-auto">
                <p className="text-xs uppercase text-[#A1A1AA]">Visualizando</p>
                <p
                  className="text-2xl font-black text-[#39FF14] tracking-tight"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                  data-testid="financial-current-period"
                >
                  {data.period.month_label} / {data.period.year}
                </p>
              </div>
            )}
          </div>
        </section>

        {loading && (
          <div className="bg-[#121212] border border-white/10 p-10 text-center text-[#A1A1AA]">
            Carregando dados do mês...
          </div>
        )}

        {!loading && error && (
          <div className="bg-[#FF3366]/10 border border-[#FF3366] p-4 text-[#FF3366]" data-testid="financial-error">
            {error}
          </div>
        )}

        {!loading && !error && data && (
          <>
            {/* Hero revenue card */}
            <section
              className={`relative overflow-hidden border p-6 md:p-8 ${
                isProfit ? 'border-[#39FF14]/60 bg-[#39FF14]/5' : 'border-[#FF3366]/60 bg-[#FF3366]/5'
              }`}
              data-testid="financial-hero"
            >
              <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className={`h-5 w-5 ${isProfit ? 'text-[#39FF14]' : 'text-[#FF3366]'}`} />
                    <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#A1A1AA]">
                      Faturamento Recebido
                    </p>
                  </div>
                  <p
                    className={`text-5xl md:text-7xl font-black tracking-tighter ${isProfit ? 'text-[#39FF14]' : 'text-[#FF3366]'}`}
                    style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                    data-testid="financial-received"
                  >
                    {formatBRL(received)}
                  </p>
                  <p className="text-sm text-[#A1A1AA] mt-2">
                    {data.revenue.paid_athletes} atleta(s) pagaram neste mês
                  </p>
                </div>
                <div className="text-left lg:text-right">
                  <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#A1A1AA] mb-1">
                    Meta Mensal
                  </p>
                  <p
                    className="text-3xl font-black text-white tracking-tight"
                    style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                  >
                    {formatBRL(target)}
                  </p>
                  <div
                    className={`inline-flex items-center gap-2 mt-2 px-3 py-1 border uppercase text-xs font-bold tracking-wider ${
                      isProfit
                        ? 'border-[#39FF14] text-[#39FF14] bg-[#39FF14]/10'
                        : 'border-[#FF3366] text-[#FF3366] bg-[#FF3366]/10'
                    }`}
                    data-testid="financial-status-badge"
                  >
                    {isProfit ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                    {isProfit ? 'No lucro' : 'Abaixo da meta'}
                    <span className="font-normal normal-case tracking-normal ml-1">
                      ({delta >= 0 ? '+' : ''}{formatBRL(delta)})
                    </span>
                  </div>
                </div>
              </div>

              {/* Progress bar */}
              <div className="mt-6">
                <div className="h-3 bg-black/60 border border-white/10 overflow-hidden">
                  <div
                    className="h-full transition-all"
                    style={{ width: `${pct}%`, backgroundColor: barColor }}
                    data-testid="financial-progress-bar"
                  />
                </div>
                <div className="flex justify-between text-[11px] text-[#A1A1AA] mt-1 uppercase tracking-wider">
                  <span>0</span>
                  <span>{pct.toFixed(0)}% da meta</span>
                  <span>{formatBRL(target)}</span>
                </div>
              </div>
            </section>

            {/* KPI grid */}
            <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-[#121212] border border-white/10 p-5" data-testid="financial-kpi-active">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="h-4 w-4 text-[#00F0FF]" />
                  <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#A1A1AA]">
                    Atletas Ativos
                  </p>
                </div>
                <p
                  className="text-4xl font-black text-white tracking-tight"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  {data.athletes.active}
                </p>
                <p className="text-[11px] text-[#A1A1AA] mt-2">
                  {data.athletes.plan_bronze} Bronze · {data.athletes.plan_prata} Prata · {data.athletes.plan_ouro} Ouro
                </p>
              </div>

              <div className="bg-[#121212] border border-white/10 p-5" data-testid="financial-kpi-paid">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="h-4 w-4 text-[#39FF14]" />
                  <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#A1A1AA]">
                    Pagamentos Confirmados
                  </p>
                </div>
                <p
                  className="text-4xl font-black text-[#39FF14] tracking-tight"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  {data.revenue.paid_athletes}
                </p>
                <p className="text-[11px] text-[#A1A1AA] mt-2">
                  Total: {formatBRL(received)}
                </p>
              </div>

              <div className="bg-[#121212] border border-[#FF3366]/30 p-5" data-testid="financial-kpi-dropouts">
                <div className="flex items-center gap-2 mb-2">
                  <UserMinus className="h-4 w-4 text-[#FF3366]" />
                  <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#A1A1AA]">
                    Atletas que Desistiram
                  </p>
                </div>
                <p
                  className="text-4xl font-black text-[#FF3366] tracking-tight"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  {data.dropouts.count}
                </p>
                <p className="text-[11px] text-[#A1A1AA] mt-2">
                  Bloqueados ou rejeitados no mês
                </p>
              </div>
            </section>

            {/* Dropouts list */}
            {data.dropouts.list.length > 0 && (
              <section className="bg-[#121212] border border-[#FF3366]/30 p-5" data-testid="financial-dropouts-list">
                <div className="flex items-center gap-2 mb-4">
                  <UserMinus className="h-4 w-4 text-[#FF3366]" />
                  <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF3366]">
                    Desistências em {data.period.month_label}/{data.period.year}
                  </p>
                </div>
                <div className="divide-y divide-white/5">
                  {data.dropouts.list.map((d) => (
                    <div key={d._id} className="py-3 flex items-center justify-between gap-3 flex-wrap">
                      <div>
                        <p className="text-white font-semibold">{d.name}</p>
                        <p className="text-xs text-[#A1A1AA]">{d.email}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span
                          className="text-[10px] px-2 py-0.5 border uppercase font-bold"
                          style={{ color: PLAN_COLORS[d.plan] || '#fff', borderColor: PLAN_COLORS[d.plan] || '#fff' }}
                        >
                          {d.plan}
                        </span>
                        <span className="text-[10px] px-2 py-0.5 border border-[#FF3366] text-[#FF3366] uppercase font-bold">
                          {d.status === 'blocked' ? 'Bloqueado' : 'Rejeitado'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Target explainer */}
            <section className="bg-[#121212] border border-white/10 p-4 text-xs text-[#A1A1AA]" data-testid="financial-target-explainer">
              <div className="flex items-center gap-2">
                <Target className="h-4 w-4 text-[#39FF14]" />
                <span>
                  Base da operação: <strong className="text-white">R$ 5.000/mês</strong>.
                  Acima disso = <span className="text-[#39FF14] font-bold">lucro</span>,
                  abaixo = <span className="text-[#FF3366] font-bold">sinal vermelho</span>.
                </span>
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
};

export default AdminFinancial;
